<template>
  <div class="fes" :class="stageClass" @mouseleave="clearFocus">
    <div class="fes-plate" :style="plateStyle" aria-hidden="true"></div>
    <div class="fes-scrim" aria-hidden="true"></div>
    <!-- The reserved bands, painted ABOVE the sectors: a sector that lights
         its own copy of the scene must not brighten the strip the fight's
         information is written on, least of all when it takes the screen. -->
    <div class="fes-bands" aria-hidden="true"></div>

    <!-- One scene, divided. Each wedge is clipped to its side's slice, carries
         that side's light and its champion, and runs out past the corners.
         Hidden from assistive tech and untabbable on purpose: everything it
         offers is offered again by the ring segment and the side's text, which
         are the real controls. -->
    <!-- The fight's own wedge, as a sector like any other except that it is
         nobody's. It has to exist in the DOM, not merely as empty space: the
         six wedges tile the screen, and without this one the pointer moving off
         a side and onto the title would leave that side lit — it entered
         nothing, so nothing told the screen to let go. -->
    <div
        class="fes-arena-sector"
        :style="{clipPath: arena.clipPath, WebkitClipPath: arena.clipPath}"
        aria-hidden="true"
        @mouseenter="clearFocus"
    ></div>

    <TransitionGroup tag="div" class="fes-sectors" name="fes-fade">
      <div
          v-for="sector in sectors"
          :key="sector.id"
          class="fes-sector"
          :class="sectorState(sector)"
          :style="sectorStyle(sector)"
          aria-hidden="true"
          @mouseenter="focus(sector.id)"
          @click="selectSide(sector.side)"
      >
        <span class="fes-sector-lit" :style="plateStyle"></span>
        <span class="fes-sector-wash"></span>
        <span class="fes-sector-figure" :style="figureStyle(sector)">
          <span class="fes-figure-bloom"></span>
          <FightSideFigure
              :character-id="sector.lead ? sector.lead.character_id : null"
              :variant="sector.side.slot"
          />
        </span>
      </div>
    </TransitionGroup>

    <!-- The six boundaries, the fight's own two among them. Felt before they
         are seen: a hairline until a side is being read, then drawn around it,
         strongest at the ring and fading toward the screen edge. -->
    <div class="fes-rays" aria-hidden="true">
      <span
          v-for="deg in boundaryDegrees"
          :key="deg"
          class="fes-ray"
          :style="{transform: `rotate(${deg}deg)`}"
      ></span>
      <template v-if="litSector">
        <span class="fes-ray is-lit" :style="{transform: `rotate(${litSector.edgeStartDeg}deg)`}"></span>
        <span class="fes-ray is-lit" :style="{transform: `rotate(${litSector.edgeEndDeg}deg)`}"></span>
      </template>
    </div>

    <!-- The fight's own area — the sixth, and the only one that is not a side.
         It is reserved in the geometry, not merely in this stylesheet: the
         sides divide the rest of the turn, so nothing is ever laid out here and
         nothing here is ever selectable. -->
    <header class="fes-head">
      <p class="fes-round">
        <span class="fes-tick" aria-hidden="true"></span>
        {{ roundLabel }}
        <span class="fes-tick" aria-hidden="true"></span>
      </p>
      <h2 class="fes-place">{{ placeLabel }}</h2>
      <p class="fes-status">{{ statusLabel }}</p>
      <span class="fes-crest" aria-hidden="true"></span>
    </header>

    <div class="fes-ring">
      <svg viewBox="0 0 100 100" class="fes-ring-svg">
        <circle class="fes-ring-track" cx="50" cy="50" r="48.8"/>

        <!-- The fight's segment. Drawn so it cannot be mistaken for a choice:
             no fill of its own, no hover, no tab stop. -->
        <g class="fes-arena" role="img" :aria-label="placeLabel">
          <path :d="arena.path" class="fes-arena-arc"/>
          <path :d="arena.path" class="fes-arena-rim"/>
          <path
              :d="ARENA_EMBLEM_PATH"
              class="fes-arena-glyph"
              :transform="glyphTransform(arena.glyphX, arena.glyphY)"
          />
        </g>

        <g role="radiogroup" :aria-label="$t('fightHud.donutTitle')">
          <g
              v-for="sector in sectors"
              :key="sector.id"
              class="fes-wedge"
              :class="sectorState(sector)"
              :style="sectorVars(sector)"
              role="radio"
              :tabindex="selectable ? 0 : -1"
              :aria-checked="sector.id === selectedSideId"
              :aria-label="wedgeLabel(sector.side)"
              @mouseenter="focus(sector.id)"
              @focus="focus(sector.id)"
              @blur="clearFocus"
              @click="selectSide(sector.side)"
              @keydown.enter.prevent="selectSide(sector.side)"
              @keydown.space.prevent="selectSide(sector.side)"
          >
            <!-- The hit area is the whole wedge out to the ring's reach, so a
                 thin band is still an easy target. -->
            <path :d="sector.hitPath" class="fes-wedge-hit"/>
            <path :d="sector.path" class="fes-wedge-arc"/>
            <path :d="sector.path" class="fes-wedge-rim"/>
            <path
                :d="sideEmblem(sector)"
                class="fes-wedge-glyph"
                :transform="glyphTransform(sector.labelX, sector.labelY)"
            />
          </g>
        </g>
      </svg>

      <!-- The centre says what the fight is doing and how long that lasts.
           Nothing else: every fact about a side belongs to that side. -->
      <div class="fes-ring-core" :class="{'is-alert': coreAlert}">
        <CountDown
            v-if="showClock"
            class="fes-core-clock"
            :deadline="deadline"
            :skew-ms="skewMs"
            @expired="onClockExpired"
        />
        <template v-else>
          <p class="fes-core-value">{{ core.value }}</p>
          <p class="fes-core-label">{{ core.label }}</p>
        </template>
      </div>
    </div>

    <!-- A side reads outward from its own segment: its mark and name, who
         leads it, and its standing in three columns. The roster waits until the
         side is looked at. -->
    <TransitionGroup tag="ul" class="fes-blocks" name="fes-fade">
      <li
          v-for="sector in sectors"
          :key="sector.id"
          class="fes-block"
          :class="sectorState(sector)"
          :style="blockStyle(sector)"
          role="radio"
          :tabindex="selectable ? 0 : -1"
          :aria-checked="sector.id === selectedSideId"
          :aria-label="wedgeLabel(sector.side)"
          @mouseenter="focus(sector.id)"
          @focusin="focus(sector.id)"
          @click="selectSide(sector.side)"
          @keydown.enter.prevent="selectSide(sector.side)"
          @keydown.space.prevent="selectSide(sector.side)"
      >
        <p class="fes-block-name">
          <svg class="fes-block-mark" viewBox="0 0 24 24" aria-hidden="true">
            <path :d="sideEmblem(sector)"/>
          </svg>
          {{ sector.side.name }}
        </p>
        <p class="fes-block-lead">{{ sector.lead ? sector.lead.name : $t('fightHud.donutNoLead') }}</p>

        <dl class="fes-stats">
          <div class="fes-stat">
            <dt>{{ $t('fightHud.donutStatFighters') }}</dt>
            <dd>{{ sector.empty ? '—' : sector.side.member_count }}</dd>
          </div>
          <div class="fes-stat">
            <dt>{{ $t('fightHud.donutStatPower') }}</dt>
            <dd>{{ sector.empty ? '—' : sector.side.power }}</dd>
          </div>
          <div class="fes-stat">
            <dt>{{ $t('fightHud.donutStatShare') }}</dt>
            <dd>{{ sector.empty ? '—' : `${sharePercent(sector.side.power_share)}%` }}</dd>
          </div>
        </dl>

        <!-- One child, because the row this collapses to zero is the whole
             detail: a second child would sit in an auto row and keep its
             height while the first collapsed, printing over the line above. -->
        <div class="fes-block-detail">
          <div class="fes-block-detail-inner">
            <p class="fes-detail-label">{{ $t('fightHud.donutRoster') }}</p>
            <ul v-if="sector.others.length" class="fes-members">
              <li v-for="member in sector.others" :key="member.character_id" class="fes-member">
                {{ member.name }}
              </li>
            </ul>
            <p v-else class="fes-members-empty">{{ $t('fightHud.donutNoMembers') }}</p>
          </div>
        </div>

        <p v-if="sector.id === pendingSideId" class="fes-block-status">
          <span class="fes-spinner" aria-hidden="true"></span>
          {{ $t('fightHud.donutJoining') }}
        </p>
        <p v-else-if="sector.isMine || sector.id === selectedSideId" class="fes-block-status">
          {{ $t('fightHud.donutYourSide') }}
        </p>
      </li>
    </TransitionGroup>

    <footer class="fes-foot">
      <HudButton
          v-if="canCreateSide"
          :type="ButtonType.SUBMIT"
          :disabled="submitting"
          class="fes-new"
          @click="createSide"
      >
        <span v-if="creating" class="fes-spinner" aria-hidden="true"></span>
        {{ creating ? $t('fightHud.donutOpeningSide') : $t('fightHud.newSide') }}
      </HudButton>
      <p v-if="error" class="fes-error" role="alert">{{ error }}</p>
      <p v-else-if="footHint" class="fes-hint" aria-live="polite">
        <span class="fes-hint-mark" aria-hidden="true">i</span>{{ footHint }}
      </p>
    </footer>
  </div>
</template>

<script>
import CountDown from '@/components/Game/Fight/CountDown.vue';
import FightSideFigure from '@/components/Fight/FightSideFigure.vue';
import HudButton from '@/components/btn/HudButton.vue';
import {ButtonType} from '@/types/ButtonType';
import {buildStageLayout} from '@/utils/sideDonutGeometry';
import {buildSideCards} from '@/utils/fightSides';
import {sideEmblemPath, ARENA_EMBLEM_PATH} from '@/utils/sideEmblems';

const MAX_FIGHT_SIDES = 5;

/* The emblem is drawn on a 24-unit square and the ring on a 100-unit one, so
   every glyph is placed by the same similarity transform. */
const GLYPH_SCALE = 0.42;

/*
 * The establishment screen: one cinematic scene divided by one radial
 * selector.
 *
 * SIX areas, and only five of them are sides. The sixth is at twelve o'clock
 * and it belongs to the fight — its round, its place, what it is doing — and it
 * is never selectable. It is reserved in `buildStageLayout`'s geometry rather
 * than by laying a band over the top of the screen, so the reservation is real:
 * the sides divide the remaining three quarters of the turn between them and no
 * side is ever laid out under the title.
 *
 * The ring is not a chart of anything. It is the control: an equal segment per
 * side, thin, large, and the origin of the whole composition — each segment
 * continues outward as the sector that holds that side's champion, so a piece
 * of ring, a character and a piece of battlefield are visibly the same thing.
 * Power is a number the side reports, not the shape of the geometry, because a
 * weak side still needs room enough to be introduced in.
 *
 * Steel and near-black throughout, and one warm note. A side is told apart by
 * its emblem, not by a hue: five colours spread across areas a quarter of the
 * screen wide read as chrome pasted over the scene, and the ember has exactly
 * one job here — marking the side being read or the side already taken.
 *
 * The screen has four states and they are whole-screen states, not control
 * states: OVERVIEW shows every side with the least information that identifies
 * it; FOCUS lifts one side out — its segment steps outward, its sector clears,
 * its character resolves, its details unfold, and the rest of the screen drops
 * away; SELECTED keeps the viewer's own side dominant and reports it in the
 * centre; LOCKED closes the ring as the window ends and lets the chosen sector
 * take the screen on the way into the fight.
 *
 * Dumb component, following `SideSelectionDonut.vue`'s precedent: no generated
 * API class is called from here. Picking a side only emits an intent —
 * `Location.vue` owns `choose_side`/`create_side` through `FightService` and
 * reports the outcome back through `fail()`/`reset()`.
 */
export default {
  name: 'FightEstablishmentStage',
  components: {CountDown, FightSideFigure, HudButton},
  setup() {
    return {ButtonType, ARENA_EMBLEM_PATH};
  },
  props: {
    sides: {
      type: Array,
      default: () => [],
    },
    selectedSideId: {
      type: String,
      default: null,
    },
    deadline: {
      type: [String, Number, Date],
      default: null,
    },
    skewMs: {
      type: Number,
      default: 0,
    },
    /* Everyone the clock reports, `FightStateDTO.participants`. Membership is
       grouped from these by `side_id` rather than sent a second time per side:
       a second copy could only ever disagree with the first. */
    participants: {
      type: Array,
      default: () => [],
    },
    /* False for a viewer inside the window with no choice to make — a founder,
       or anyone already seated. They watch the fight form; the server would
       refuse them `choose_side`, so the screen must not offer it. */
    selectable: {
      type: Boolean,
      default: true,
    },
    selfCharacterId: {
      type: String,
      default: null,
    },
    locationName: {
      type: String,
      default: null,
    },
    round: {
      type: Number,
      default: null,
    },
    backgroundUrl: {
      type: String,
      default: '',
    },
    /* What kind of fight this is, straight off `FightStateDTO`. Named in the
       header band because it changes what taking a side means. */
    duel: {
      type: Boolean,
      default: false,
    },
    training: {
      type: Boolean,
      default: false,
    },
    sealed: {
      type: Boolean,
      default: false,
    },
    /* The window is closing and the sides are being locked in. The screen
       stops being a chooser and starts being the way into the fight. */
    locked: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select-side', 'create-side', 'expired'],
  data() {
    return {
      submitting: false,
      error: null,
      focusedSideId: null,
      clockExpired: false,
      /* Which side this viewer is waiting on an answer for. `submitting` says
         the screen is busy; this says where the work is happening, so the
         spinner appears on the side actually being joined. */
      pendingSideId: null,
      creating: false,
    };
  },
  watch: {
    deadline: {
      immediate: true,
      handler(value) {
        this.clockExpired = !!value && new Date(value).getTime() - (Date.now() + this.skewMs) <= 0;
      },
    },
  },
  computed: {
    /* Once the window is locked there is nothing left to count: the centre
       belongs to the confirmation, not to a dial that no longer decides
       anything. */
    showClock() {
      return !!this.deadline && !this.clockExpired && !this.isLocking;
    },
    /* The whole screen in one shape: the fight's reserved wedge, and one entry
       per side carrying both halves of what a side needs — the geometry of its
       wedge and the people standing in it. */
    layout() {
      return buildStageLayout(this.sides);
    },
    arena() {
      return this.layout.arena;
    },
    sectors() {
      const geometry = this.layout.sectors;
      const cards = buildSideCards(geometry, this.participants, {
        selfCharacterId: this.selfCharacterId,
        unknownLeadName: this.$t('fightHud.donutUnknownLead'),
      });
      return geometry.map((sector, index) => ({
        ...sector,
        ...cards[index],
        /* A side nobody has joined has no standing to report, and printing a
           zero next to a rival's two hundred says something the fight has not
           decided yet. It reports a dash. */
        empty: !sector.side.member_count,
      }));
    },
    /* Every boundary on the screen: each side's opening ray, plus the one that
       closes the fight's own wedge. Six lines for six areas. */
    boundaryDegrees() {
      return [...this.sectors.map(sector => sector.rayDeg), this.arena.edgeStartDeg];
    },
    /* OVERVIEW → FOCUS → SELECTED → LOCKED. One value, so the sector, the
       segment, the separators, the centre and the footer can never disagree
       about what the screen is doing. */
    stageState() {
      if (this.isLocking) return 'locked';
      if (this.focusedSideId) return 'focused';
      if (this.ownSideId) return 'selected';
      return 'overview';
    },
    /*
     * The window is over — and only the caller can say that, because only the
     * server knows.
     *
     * An expired clock is NOT the signal. The phase turns over when the server
     * turns it over, and it can sit past its own deadline: everyone acked and
     * the next phase has not opened yet, or the fight worker is not running at
     * all. Locking on the deadline would strand that viewer on a screen
     * showing one character and no way back to the other sides, for a window
     * that is in fact still open.
     */
    isLocking() {
      return this.locked;
    },
    stageClass() {
      return [
        `is-${this.stageState}`,
        {
          'is-readonly': !this.selectable,
          'has-focus': !!this.focusedSideId,
          'is-seated': !!this.ownSideId,
          'is-busy': this.submitting,
        },
      ];
    },
    /* The side whose boundaries are drawn: the one being read, or failing
       that the viewer's own. */
    litSector() {
      const id = this.focusedSideId || this.ownSideId;
      return this.sectors.find(sector => sector.id === id) || null;
    },
    canCreateSide() {
      return this.selectable !== false && this.sides.length < MAX_FIGHT_SIDES;
    },
    /* The side this viewer is already on — theirs by membership, or the one
       they just picked. It is the one thing on this screen that never dims. */
    ownSideId() {
      const mine = this.sectors.find(sector => sector.isMine);
      return mine ? mine.id : this.selectedSideId;
    },
    ownSideName() {
      const own = this.sectors.find(sector => sector.id === this.ownSideId);
      return own ? own.side.name : null;
    },
    /*
     * The centre while the window runs is the clock and nothing else, because
     * that is the only fact the centre owns: how long is left. Every number
     * about a side belongs beside that side, and a headcount printed here as
     * well as in five `БІЙЦІ` columns is the same fact said twice.
     *
     * Once there is no clock it answers the question the clock was answering —
     * what the window is doing, or what the viewer answered it.
     */
    core() {
      if (this.isLocking) {
        return {
          value: this.ownSideName || this.$t('fightHud.donutLocking'),
          label: this.$t('fightHud.donutLocked'),
        };
      }
      if (this.ownSideName) {
        return {value: this.ownSideName, label: this.$t('fightHud.donutSelected')};
      }
      return {value: '—', label: this.$t('fightHud.donutLockingSides')};
    },
    coreAlert() {
      return this.isLocking;
    },
    /* The one line under the fight's name, and it says what the fight is doing.
       A kind is named only when it is not the ordinary case — a duel, a
       training bout or a sealed fight change what taking a side means, and an
       open battle does not need announcing. */
    statusLabel() {
      if (this.isLocking) return this.$t('fightHud.donutFightStarting');
      if (this.training) return this.$t('fightHud.trainingTitle');
      if (this.duel) return this.$t('fightHud.donutKindDuel');
      if (this.sealed) return this.$t('fightHud.donutSealed');
      return this.$t('fightHud.donutWatchTitle');
    },
    /*
     * One line, and it says the thing that is true right now that nothing else
     * on the screen is already saying.
     *
     * Seated, it says nothing at all. Naming their side would be a third copy
     * of what the centre and the side's own tag already say, and inviting them
     * to hover would be worse than redundant — the selector stops answering the
     * pointer once they are in, so the line would be an instruction the screen
     * refuses to follow. Locked, likewise: the centre and the header have both
     * spoken.
     *
     * The hover invitation is for the one viewer it is still true for: someone
     * watching a fight form that they cannot join.
     */
    footHint() {
      if (this.submitting) return this.$t('fightHud.donutWorking');
      if (this.isLocking || this.ownSideName) return '';
      if (!this.selectable) return this.$t('fightHud.donutHoverHint');
      return this.$t('fightHud.donutPickHint');
    },
    placeLabel() {
      return this.locationName || this.$t('fightStart.unknownLocation');
    },
    roundLabel() {
      return this.$t('fightStart.round', {round: this.round ?? 1});
    },
    plateStyle() {
      return this.backgroundUrl
          ? {backgroundImage: `url(${this.backgroundUrl})`}
          : {};
    },
  },
  methods: {
    /* The window can outlive its own clock — the phase only turns over when
       the server says so. The screen stops pretending to count and hands the
       centre back to the fight's state, and still tells the caller. */
    onClockExpired() {
      this.clockExpired = true;
      this.$emit('expired');
    },
    /* Once the viewer is seated the screen stops being a chooser. Nothing
       lights, nothing dims and nothing answers the pointer: the question has
       been answered, and a control that still reacts invites an answer it can
       no longer take. */
    focus(sideId) {
      if (this.isLocking || this.ownSideId) return;
      this.focusedSideId = sideId;
    },
    clearFocus() {
      this.focusedSideId = null;
    },
    sectorState(sector) {
      return {
        'is-focused': sector.id === this.focusedSideId,
        'is-selected': sector.id === this.selectedSideId,
        'is-mine': sector.isMine,
        /* Pinned means "never fade this one". A viewer who has already taken a
           side keeps looking at it while they read the others — dimming the
           side they are standing on to read a rival is the wrong picture. */
        'is-pinned': !!this.ownSideId && sector.id === this.ownSideId,
        'is-pending': sector.id === this.pendingSideId,
      };
    },
    /* Every layer of a side takes the same custom properties, so the segment,
       the sector, the figure and the text all move on the same bearing. */
    sectorVars(sector) {
      return {
        '--fes-out-x': sector.outX,
        '--fes-out-y': sector.outY,
        /* Where this side's attention sits — its champion. The sharpened
           scene is bloomed around that point so it fades out before the wedge
           boundary, instead of ending on a straight cut across the screen. */
        '--fes-focus-x': `${sector.figureX}%`,
        '--fes-focus-y': `${sector.figureY}%`,
      };
    },
    sectorStyle(sector) {
      return {
        ...this.sectorVars(sector),
        clipPath: sector.clipPath,
        WebkitClipPath: sector.clipPath,
      };
    },
    figureStyle(sector) {
      return {'--fes-x': `${sector.figureX}%`, '--fes-y': `${sector.figureY}%`};
    },
    blockStyle(sector) {
      return {
        ...this.sectorVars(sector),
        '--fes-x': `${sector.anchorX}%`,
        '--fes-y': `${sector.anchorY}%`,
      };
    },
    sideEmblem(sector) {
      return sideEmblemPath(sector.side?.slot);
    },
    /* An emblem is authored on a 24-unit square; the ring is a 100-unit one.
       Centre it on the point the geometry named. */
    glyphTransform(x, y) {
      const offset = (24 * GLYPH_SCALE) / 2;
      return `translate(${(x - offset).toFixed(2)} ${(y - offset).toFixed(2)}) scale(${GLYPH_SCALE})`;
    },
    selectSide(side) {
      if (this.submitting || !side || this.selectable === false || this.ownSideId) {
        return;
      }
      this.submitting = true;
      this.pendingSideId = side.id;
      this.error = null;
      this.$emit('select-side', side.id);
    },
    createSide() {
      if (this.submitting || !this.canCreateSide) {
        return;
      }
      this.submitting = true;
      this.creating = true;
      this.error = null;
      this.$emit('create-side');
    },
    sharePercent(share) {
      return Math.round((share ?? 0) * 100);
    },
    wedgeLabel(side) {
      return this.$t('fightHud.donutSideAriaLabel', {
        name: side.name,
        count: side.member_count,
        percent: this.sharePercent(side.power_share),
      });
    },
    // Exposed for the caller: this component performs no I/O of its own, so
    // whoever owns the choose/create-side call reports the outcome back here.
    fail(message) {
      this.submitting = false;
      this.pendingSideId = null;
      this.creating = false;
      this.error = message || this.$t('fightHud.error');
    },
    reset() {
      this.submitting = false;
      this.pendingSideId = null;
      this.creating = false;
      this.error = null;
    },
  },
};
</script>

<style scoped>
/*
 * Near-black, graphite, steel — and one warm note.
 *
 * There is no side palette here. A slot colour is right for a mark the size of
 * a token ring; spread across a wedge a quarter of the screen wide it reads as
 * HUD chrome laid over the battlefield, and five of them at once read as a
 * chart. The sides are told apart by their emblems, every surface is the same
 * steel, and the ember is spent on exactly two things: the side being read, and
 * the side this viewer has taken.
 */
.fes {
  --fes-void: #04070a;
  --fes-hairline: rgba(150, 172, 184, 0.16);
  --fes-ember: #d8a44c;
  --fes-steel: #9fb2bd;
  --fes-wedge: #0d151b;
  --fes-ink: #e7eef1;
  --fes-ink-muted: #8b979e;
  --fes-ink-dim: #5f6b72;
  --fes-head-band: 20%;

  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--fes-void);
  font-family: var(--pl-font-body, 'IBM Plex Sans', system-ui, sans-serif);
  color: var(--fes-ink);
}

/* The scene, held back so nothing in it competes with what is written over
   it. The focused sector gets a sharper copy of the same plate, which is what
   makes the battlefield itself seem to come into focus. */
.fes-plate {
  position: absolute;
  inset: 0;
  background:
      radial-gradient(circle at 50% 42%, rgba(58, 92, 110, 0.18), transparent 64%),
      linear-gradient(168deg, #060b0f 0%, #0b1319 55%, #04070a 100%);
  background-size: cover;
  background-position: center;
  filter: saturate(0.55) brightness(0.62);
}

/* Three jobs, one layer: hold the middle dark enough for the ring to sit on,
   vignette the corners, and darken the reserved top and bottom bands so the
   fight's information and its controls stay legible over any scene. */
.fes-scrim {
  position: absolute;
  inset: 0;
  background:
      radial-gradient(circle at 50% 50%, rgba(4, 7, 10, 0.8) 0%, rgba(4, 7, 10, 0.3) 30%, transparent 54%),
      radial-gradient(circle at 50% 50%, transparent 52%, rgba(3, 6, 8, 0.72) 100%);
  pointer-events: none;
}

.fes-bands {
  position: absolute;
  inset: 0;
  z-index: 2;
  background:
      linear-gradient(to bottom, rgba(4, 7, 10, 0.96) 0%, rgba(4, 7, 10, 0.74) 13%, transparent 27%),
      linear-gradient(to top, rgba(4, 7, 10, 0.94) 0%, rgba(4, 7, 10, 0.55) 9%, transparent 21%);
  pointer-events: none;
}

/* Everything the viewer reads or clicks sits above the bands; the scene and
   its sectors sit below them. */
.fes-head,
.fes-ring,
.fes-blocks,
.fes-foot {
  z-index: 3;
}

.fes-sectors,
.fes-rays {
  position: absolute;
  inset: 0;
}

/*
 * Neither container tiles anything — only their clipped or rotated children do,
 * so neither may take the pointer on its way to them.
 *
 * `.fes-rays` is the one that mattered: a full-bleed box painted after the
 * sectors, so every click aimed at a side's own area landed on the separators
 * instead and a side could only be taken by hitting its ring segment or its
 * name. The rays themselves were already transparent to the pointer; the box
 * holding them was not.
 */
.fes-sectors,
.fes-rays {
  pointer-events: none;
}

.fes-sector {
  pointer-events: auto;
}

.fes-arena-sector {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.fes-sector {
  position: absolute;
  inset: 0;
  cursor: pointer;
  transition: opacity 260ms ease, transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

.fes.is-readonly .fes-sector,
.fes.is-readonly .fes-wedge,
.fes.is-readonly .fes-block {
  cursor: default;
}

/*
 * Seated, the selector stops being one. It takes no pointer at all — no hover
 * light, no dimming, no cursor promising a click that `selectSide` would now
 * refuse. What is left is a picture of a fight the viewer is already in.
 *
 * The sides they did not take are recessed, not hidden. They are still four
 * fifths of what is about to happen and have to stay readable; only enough
 * separation to put the side they are standing in in front of them.
 */
.fes.is-seated .fes-sector,
.fes.is-seated .fes-wedge,
.fes.is-seated .fes-block,
.fes.is-seated .fes-arena-sector {
  cursor: default;
  pointer-events: none;
}

.fes.is-seated .fes-sector:not(.is-pinned),
.fes.is-seated .fes-block:not(.is-pinned) {
  opacity: 0.92;
}

/* The recession is carried by the ember and the light on the pinned side, not
   by draining the others: a rival at two thirds strength on top of a figure
   already held back in grey reads as removed from the fight rather than as
   second in it. Their champions keep most of their colour. */
.fes.is-seated .fes-sector:not(.is-pinned) .fes-sector-figure {
  filter: grayscale(0.3) brightness(0.9) contrast(1.05);
  opacity: 0.92;
}

/*
 * Nothing is faded until something is being looked at. In the overview every
 * side stands at full strength, and being seated does not dim anybody — a
 * viewer who has taken a side still has to be able to read the rest of the
 * fight. Only a hover separates: the side under the pointer holds, everything
 * else recedes, and the moment the pointer leaves the screen is whole again.
 *
 * Focus is a camera move, not a control state: the side being read steps
 * outward along its own bearing while the rest fall back.
 */
.fes.has-focus .fes-sector {
  opacity: 0.2;
}

.fes.has-focus .fes-sector.is-focused {
  opacity: 1;
  transform: translate(calc(var(--fes-out-x) * 8px), calc(var(--fes-out-y) * 8px));
}

.fes-sector-lit {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: saturate(0.95) brightness(1.02);
  -webkit-mask-image: radial-gradient(
      circle at var(--fes-focus-x) var(--fes-focus-y),
      #000 0%,
      rgba(0, 0, 0, 0.75) 34%,
      transparent 72%
  );
  mask-image: radial-gradient(
      circle at var(--fes-focus-x) var(--fes-focus-y),
      #000 0%,
      rgba(0, 0, 0, 0.75) 34%,
      transparent 72%
  );
  opacity: 0;
  transition: opacity 300ms ease;
}

.fes-sector.is-focused .fes-sector-lit {
  opacity: 0.85;
}

/* The wash spills outward from the ring and decays before the screen edge, so
   the light has a source and the cut between two sectors is never the
   brightest thing in it. */
.fes-sector-wash {
  position: absolute;
  inset: 0;
  background: radial-gradient(
      circle at 50% 50%,
      transparent 24%,
      rgba(88, 118, 134, 0.1) 33%,
      transparent 74%
  );
  opacity: 0.5;
  transition: opacity 260ms ease, background 260ms ease;
}

.fes-sector.is-focused .fes-sector-wash,
.fes-sector.is-pinned .fes-sector-wash {
  opacity: 1;
  background: radial-gradient(
      circle at 50% 50%,
      transparent 23%,
      rgba(216, 164, 76, 0.14) 33%,
      rgba(216, 164, 76, 0.05) 56%,
      transparent 86%
  );
}

.fes-sector-figure {
  position: absolute;
  left: clamp(8%, var(--fes-x), 92%);
  top: clamp(30%, var(--fes-y), 84%);
  width: clamp(15rem, 25vw, 28rem);
  transform: translate(-50%, -50%);
  filter: grayscale(0.62) brightness(0.72) contrast(1.05);
  opacity: 0.78;
  transition: filter 300ms ease, opacity 300ms ease, transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

/* The light a side's champion stands in when it is the one being read. */
.fes-figure-bloom {
  position: absolute;
  left: 50%;
  top: 58%;
  width: 92%;
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(216, 164, 76, 0.34) 0%, transparent 68%);
  opacity: 0;
  transition: opacity 320ms ease;
  pointer-events: none;
}

.fes-sector.is-focused .fes-sector-figure,
.fes-sector.is-pinned .fes-sector-figure {
  filter: grayscale(0.08) brightness(1) contrast(1.06);
  opacity: 1;
}

.fes-sector.is-focused .fes-sector-figure {
  transform: translate(-50%, -50%) scale(1.05);
}

.fes-sector.is-focused .fes-figure-bloom,
.fes-sector.is-pinned .fes-figure-bloom {
  opacity: 0.5;
}

/* Felt before seen: a hairline at a tenth of its strength until a side is
   being read, and always strongest at the ring, fading toward the edge. */
.fes-ray {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100vmax;
  height: 1px;
  transform-origin: 0 50%;
  background: linear-gradient(90deg, transparent 0 19%, rgba(190, 210, 221, 0.9) 24%, rgba(150, 172, 184, 0.42) 66%, transparent 97%);
  opacity: 0.36;
  transition: opacity 260ms ease;
  pointer-events: none;
}

.fes-ray.is-lit {
  opacity: 0.6;
}

.fes.is-selected .fes-ray.is-lit,
.fes.is-locked .fes-ray.is-lit {
  opacity: 0.62;
}

/* ---- the fight's own area ----
   Set to the width the reserved wedge actually has. The wedge is a quarter
   turn about the screen centre, so its two rays reach the top edge a quarter
   and three quarters of the way across; type wider than that would spill out
   of the area it is supposed to occupy and the geometry would stop being
   readable as six. */
.fes-head {
  position: absolute;
  top: 0;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  width: min(46rem, 46vw);
  height: var(--fes-head-band);
  padding-top: 0.75rem;
  transform: translateX(-50%);
  text-align: center;
  pointer-events: none;
}

.fes-round {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0;
  font-size: var(--pl-text-label, 0.6rem);
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: var(--fes-ink-muted);
}

.fes-tick {
  width: 1.6rem;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(200, 216, 224, 0.5), transparent);
}

.fes-place {
  margin: 0;
  font-family: var(--pl-font-display, var(--gm-font-display));
  font-size: clamp(1.4rem, 3.2vw, 2.7rem);
  font-weight: 700;
  letter-spacing: 0.14em;
  line-height: 1.08;
  text-transform: uppercase;
  color: var(--fes-ink);
  text-shadow: 0 0.4rem 2rem rgba(0, 0, 0, 0.9);
}

.fes-status {
  margin: 0;
  font-size: var(--pl-text-xs, 0.72rem);
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--fes-ink-muted);
}

/* Closes the fight's area off from the ring below it, the way the reserved
   wedge closes it off from the sides either side. */
.fes-crest {
  position: relative;
  width: min(20rem, 60%);
  height: 1px;
  margin-top: 0.35rem;
  background: linear-gradient(90deg, transparent, var(--fes-hairline) 22%, var(--fes-hairline) 78%, transparent);
}

.fes-crest::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0.3rem;
  height: 0.3rem;
  border: 1px solid rgba(200, 216, 224, 0.45);
  background: var(--fes-void);
  transform: translate(-50%, -50%) rotate(45deg);
}

/* ---- the radial selector ----
   Centred on the screen, because the sectors are: the wedges are clipped about
   the box's own middle, so a ring anywhere else would have the rays leaving
   from a point the ring is not on. */
.fes-ring {
  position: absolute;
  left: 50%;
  top: 50%;
  width: clamp(17.5rem, 23vw, 22rem);
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
}

.fes-ring-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.fes-ring-track {
  fill: none;
  stroke: rgba(150, 172, 184, 0.12);
  stroke-width: 0.25;
}

.fes-wedge {
  cursor: pointer;
  outline: none;
  transition: transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

/* The segment steps out of the ring toward its own sector. */
.fes-wedge.is-focused {
  transform: translate(calc(var(--fes-out-x) * 2.2px), calc(var(--fes-out-y) * 2.2px));
}

.fes-wedge-hit {
  fill: transparent;
  stroke: none;
}

.fes-wedge-arc {
  fill: var(--fes-wedge);
  stroke: none;
  transition: fill 220ms ease;
}

.fes-wedge-rim {
  fill: none;
  stroke: rgba(159, 178, 189, 0.32);
  stroke-width: 0.5;
  transition: stroke 220ms ease, stroke-width 220ms ease;
}

.fes-wedge-glyph {
  fill: none;
  stroke: rgba(159, 178, 189, 0.5);
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
  transition: stroke 220ms ease;
  pointer-events: none;
}

/* Reading a side and having taken one look the same on the ring, and that is
   the point: the ember says "this one", whether because the pointer is on it
   or because the viewer is standing in it. */
.fes-wedge.is-focused .fes-wedge-arc,
.fes-wedge:hover .fes-wedge-arc,
.fes-wedge.is-pinned .fes-wedge-arc {
  fill: #241c10;
}

.fes-wedge.is-focused .fes-wedge-rim,
.fes-wedge:hover .fes-wedge-rim,
.fes-wedge.is-selected .fes-wedge-rim,
.fes-wedge.is-pinned .fes-wedge-rim {
  stroke: var(--fes-ember);
  stroke-width: 1;
}

.fes-wedge.is-focused .fes-wedge-glyph,
.fes-wedge:hover .fes-wedge-glyph,
.fes-wedge.is-pinned .fes-wedge-glyph {
  stroke: var(--fes-ember);
}

.fes-wedge:focus-visible .fes-wedge-rim {
  stroke: var(--fes-ink);
  stroke-width: 1.3;
}

/* The fight's segment: the same band, held open. No fill, a dashed rim and a
   muted mark — a place on the ring rather than a party to be chosen. */
.fes-arena {
  pointer-events: none;
}

.fes-arena-arc {
  fill: rgba(10, 16, 21, 0.55);
  stroke: none;
}

.fes-arena-rim {
  fill: none;
  stroke: rgba(159, 178, 189, 0.22);
  stroke-width: 0.4;
  stroke-dasharray: 1.6 1.6;
}

.fes-arena-glyph {
  fill: none;
  stroke: rgba(159, 178, 189, 0.38);
  stroke-width: 1.4;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}

.fes-wedge.is-pending .fes-wedge-arc {
  animation: fes-pulse 1.1s ease-in-out infinite;
}

/* The centre carries the fight's state. No plate, no ring of its own: the
   thin band already draws the circle. */
.fes-ring-core {
  position: absolute;
  inset: 24%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
}

/*
 * One size, in every state.
 *
 * It used to step down when a side's name replaced the figure, which meant the
 * middle of the ring changed weight as the screen moved between states — and,
 * because the step was keyed to `is-selected` alone, reading another side while
 * seated snapped it back up. A name that will not fit is cut instead: the side
 * is named in full beside its own segment, so the centre only has to say which
 * one, and a ring whose type resizes under the pointer reads as a glitch.
 */
.fes-core-value {
  /* The core box is a square inscribed in the ring's hole, but a name sits on
     the centre line where the hole is at full width — so the line is allowed
     the diameter the square gives up, and one more word fits before it has to
     be cut. */
  width: 112%;
  margin: 0 -6%;
  font-family: var(--pl-font-display, var(--gm-font-display));
  font-size: clamp(1rem, 1.45vw, 1.4rem);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--fes-ink);
}

.fes-core-label {
  margin: 0.4rem 0 0;
  font-size: var(--pl-text-micro, 0.56rem);
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--fes-ink-muted);
}

.fes-ring-core.is-alert .fes-core-value {
  color: var(--fes-ember);
}

/*
 * While it runs, the clock IS the centre — the one thing the middle of the ring
 * knows that no side does, so it is set at the size of the fact it carries
 * rather than as a HUD widget parked in the middle.
 *
 * Retuned off the tactical HUD's neon cyan and its glow, which belong to a
 * board overlay and not to a cinematic plate. The escalation is untouched:
 * yellow under fifteen seconds, red under five, and those still have to arrive
 * instantly.
 */
.fes-core-clock {
  --dial: #cfe0e8;
  width: 100%;
  height: 100%;
}

.fes-core-clock :deep(.countdown__track) {
  fill: rgba(5, 9, 12, 0.86);
  stroke: rgba(150, 172, 184, 0.14);
  stroke-width: 2;
}

.fes-core-clock :deep(.countdown__sweep) {
  stroke-width: 2;
  filter: none;
}

.fes-core-clock :deep(.countdown__face) {
  font-family: var(--pl-font-display, var(--gm-font-display));
}

.fes-core-clock :deep(.countdown__value) {
  font-size: clamp(2.2rem, 3.4vw, 3.4rem);
  color: var(--fes-ink);
  text-shadow: 0 0.3rem 1.6rem rgba(0, 0, 0, 0.9);
}

.fes-core-clock :deep(.countdown__unit) {
  margin-top: 0.35rem;
  font-size: var(--pl-text-micro, 0.56rem);
  letter-spacing: 0.34em;
  color: var(--fes-ink-muted);
}

/* ---- the sides ---- */
.fes-blocks {
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  pointer-events: none;
}

/* No card and no border box: a colour mark, a name, and the scene's own dark
   bloomed behind the type so it holds up when a character stands behind it. */
.fes-block {
  position: absolute;
  left: clamp(9.5rem, var(--fes-x), calc(100% - 9.5rem));
  top: clamp(26%, var(--fes-y), 80%);
  width: clamp(11rem, 16vw, 15rem);
  /* One step, and every gap inside the block is it or twice it. The block used
     to set six different values by eye and sit flush against the left edge of
     its own backdrop, so the type touched the point the dark stopped. */
  --fes-step: 0.3rem;
  padding: calc(var(--fes-step) * 2) calc(var(--fes-step) * 3) calc(var(--fes-step) * 2) calc(var(--fes-step) * 2.5);
  transform: translate(-50%, -50%);
  background: radial-gradient(115% 130% at 0% 50%, rgba(4, 7, 10, 0.82) 0%, rgba(4, 7, 10, 0.42) 58%, transparent 100%);
  cursor: pointer;
  outline: none;
  pointer-events: auto;
  transition: opacity 260ms ease, transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

.fes.has-focus .fes-block {
  opacity: 0.24;
}

.fes.has-focus .fes-block.is-focused {
  opacity: 1;
  transform: translate(-50%, -50%) translate(calc(var(--fes-out-x) * 8px), calc(var(--fes-out-y) * 8px));
}

.fes-block-name {
  display: flex;
  align-items: center;
  gap: calc(var(--fes-step) * 1.5);
  margin: 0;
  line-height: 1.15;
  font-family: var(--pl-font-display, var(--gm-font-display));
  font-size: clamp(0.85rem, 1.2vw, 1.05rem);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--fes-ink);
}

/* The same emblem the side wears on the ring, at the size a name is set in —
   so a segment and a heading are recognisably one side without a colour having
   to say so. */
.fes-block-mark {
  width: 1.05rem;
  height: 1.05rem;
  flex-shrink: 0;
  fill: none;
  stroke: rgba(159, 178, 189, 0.62);
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: stroke 220ms ease;
}

.fes-block.is-focused .fes-block-mark,
.fes-block.is-pinned .fes-block-mark {
  stroke: var(--fes-ember);
}

.fes-block.is-focused .fes-block-name {
  color: #fff;
}

.fes-block-lead {
  margin: var(--fes-step) 0 0;
  font-size: var(--pl-text-xs, 0.72rem);
  line-height: 1.2;
  color: var(--fes-ink-muted);
}

.fes-block.is-focused .fes-block-lead,
.fes-block.is-pinned .fes-block-lead {
  color: var(--fes-ember);
}

/* A side's standing, in the overview and not on hover: three headed columns
   under a rule, read straight across, and comparable at a glance with the
   other four because every side sets them in the same three places. */
.fes-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0 calc(var(--fes-step) * 1.5);
  margin: calc(var(--fes-step) * 2) 0 0;
  padding-top: calc(var(--fes-step) * 1.5);
  border-top: 1px solid var(--fes-hairline);
}

.fes-stat dt {
  font-size: var(--pl-text-micro, 0.56rem);
  letter-spacing: 0.14em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--fes-ink-dim);
}

.fes-stat dd {
  margin: var(--fes-step) 0 0;
  font-family: var(--pl-font-mono, ui-monospace, monospace);
  font-size: var(--pl-text-sm, 0.8rem);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  color: var(--fes-ink);
}

.fes-block.is-pinned .fes-stat dd {
  color: var(--fes-ember);
}

/* The detail is the reward for looking, so it is not laid out until then. */
.fes-block-detail {
  display: grid;
  grid-template-rows: 0fr;
  margin-top: 0;
  opacity: 0;
  transition: grid-template-rows 260ms ease, opacity 260ms ease, margin-top 260ms ease;
}

.fes-block.is-focused .fes-block-detail {
  grid-template-rows: 1fr;
  margin-top: calc(var(--fes-step) * 2);
  opacity: 1;
}

.fes-block-detail-inner {
  min-height: 0;
  overflow: hidden;
}

.fes-detail-label {
  margin: 0 0 calc(var(--fes-step) * 1.5);
  padding-top: calc(var(--fes-step) * 1.5);
  border-top: 1px solid var(--fes-hairline);
  font-size: var(--pl-text-micro, 0.56rem);
  letter-spacing: 0.16em;
  line-height: 1;
  text-transform: uppercase;
  color: var(--fes-ink-dim);
}

.fes-members {
  display: flex;
  flex-wrap: wrap;
  gap: var(--fes-step);
  margin: 0;
  padding: 0;
  list-style: none;
}

.fes-member {
  padding: calc(var(--fes-step) * 0.5) calc(var(--fes-step) * 1.5);
  border: 1px solid var(--fes-hairline);
  font-size: var(--pl-text-micro, 0.56rem);
  line-height: 1.2;
  color: var(--fes-ink-muted);
  white-space: nowrap;
}

.fes-members-empty {
  margin: 0;
  font-size: var(--pl-text-micro, 0.56rem);
  font-style: italic;
  color: var(--fes-ink-dim);
}

.fes-block-status {
  display: flex;
  align-items: center;
  gap: calc(var(--fes-step) * 1.5);
  margin: calc(var(--fes-step) * 2) 0 0;
  line-height: 1;
  font-size: var(--pl-text-micro, 0.56rem);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--fes-ember);
}

/* ---- the reserved footer band ---- */
/* The band is a caption, not a lid. It sits over the bottom side's sector, so
   only the control inside it takes the pointer — otherwise a click meant for
   that side would land on a line of prose. */
.fes-foot {
  position: absolute;
  bottom: clamp(2rem, 6vh, 4rem);
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  transform: translateX(-50%);
  pointer-events: none;
}

.fes-new {
  min-width: 8rem;
  pointer-events: auto;
}

.fes-hint {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0;
  font-size: var(--pl-text-label, 0.6rem);
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--fes-ink-muted);
}

.fes-hint-mark {
  display: grid;
  place-items: center;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  border: 1px solid var(--fes-hairline);
  border-radius: 50%;
  font-family: var(--pl-font-display, var(--gm-font-display));
  font-size: 0.55rem;
  letter-spacing: 0;
  text-transform: none;
  color: var(--fes-ink-dim);
}

.fes.is-busy .fes-hint {
  color: var(--fes-ember);
}

.fes-error {
  margin: 0;
  font-size: var(--pl-text-xs, 0.72rem);
  color: var(--pl-danger, #e0645c);
}

/* Work in progress is shown where the work is: on the side being joined, and
   on the control that opened a new one. */
.fes-spinner {
  width: 0.6rem;
  height: 0.6rem;
  flex-shrink: 0;
  border: 1px solid color-mix(in srgb, var(--fes-ember) 35%, transparent);
  border-top-color: var(--fes-ember);
  border-radius: 50%;
  animation: fes-spin 0.7s linear infinite;
}

.fes.is-busy .fes-sector,
.fes.is-busy .fes-block,
.fes.is-busy .fes-wedge {
  cursor: progress;
}

/* ---- locked: the window is over ----
   The ring closes down to the centre's confirmation and the sector that won
   the viewer comes forward. What it must NOT do is empty the screen: this is
   the last thing shown before the first round, and a fighter who cannot see
   who is standing opposite has been told less at the moment of commitment than
   they were told while still choosing. The sides they did not take are
   recessed exactly as they are when seated — held back, still readable. */
.fes.is-locked .fes-sector,
.fes.is-locked .fes-wedge,
.fes.is-locked .fes-block {
  pointer-events: none;
}

.fes.is-locked .fes-sector:not(.is-pinned),
.fes.is-locked .fes-block:not(.is-pinned) {
  opacity: 0.82;
}

.fes.is-locked .fes-sector:not(.is-pinned) .fes-sector-figure {
  filter: grayscale(0.35) brightness(0.85) contrast(1.05);
}

.fes.is-locked .fes-sector.is-pinned {
  opacity: 1;
}

.fes.is-locked .fes-sector.is-pinned .fes-sector-figure {
  transform: translate(-50%, -50%) scale(1.12);
}

/* Held below the focus level. The lift now stays inside its own wedge rather
   than taking the screen, and at full strength the clip boundary reads as a
   line drawn across the scene instead of as light coming off a champion. */
.fes.is-locked .fes-sector.is-pinned .fes-sector-lit {
  opacity: 0.55;
}

.fes.is-locked .fes-wedge:not(.is-pinned) {
  opacity: 0.45;
}

/* The fight's area has said what it had to say in the header. Left drawn on a
   ring that has otherwise emptied, its dashed arc is the only chrome on the
   screen at the moment the fight is meant to take it. */
.fes.is-locked .fes-arena,
.fes.is-locked .fes-ring-track {
  opacity: 0;
}

.fes-arena,
.fes-ring-track {
  transition: opacity 420ms ease;
}

.fes.is-locked .fes-ring {
  transform: translate(-50%, -50%) scale(0.86);
}

.fes.is-locked .fes-block.is-pinned {
  opacity: 1;
}

.fes-ring,
.fes-sector-lit {
  transition: transform 520ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity 420ms ease;
}

@keyframes fes-spin {
  to { transform: rotate(360deg); }
}

@keyframes fes-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}

/* A side that opens mid-window arrives rather than appears. */
.fes-fade-enter-active {
  transition: opacity 420ms ease;
}

.fes-fade-leave-active {
  transition: opacity 220ms ease;
  position: absolute;
}

.fes-fade-enter-from,
.fes-fade-leave-to {
  opacity: 0;
}

.fes-fade-move {
  transition: transform 380ms ease;
}

/* Below this, the radial split has nowhere to put five sides: the ring keeps
   the composition and the sides become a list under it. */
@media (max-width: 900px), (max-height: 620px) {
  .fes {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .fes-sectors,
  .fes-rays {
    display: none;
  }

  /* Relative, not static: the plate and the scrim are still positioned, and a
     non-positioned sibling would paint underneath them however late it comes
     in the document. */
  .fes-head,
  .fes-ring,
  .fes-blocks,
  .fes-foot,
  .fes-block {
    position: relative;
    z-index: 1;
    inset: auto;
    height: auto;
    transform: none;
  }

  .fes-ring {
    width: min(62vw, 15rem);
  }

  .fes-blocks {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    gap: 0.75rem;
    width: 100%;
    max-width: 42rem;
  }

  .fes-block {
    width: auto;
    opacity: 1;
    background: none;
    border-left: 1px solid var(--fes-hairline);
    padding: calc(var(--fes-step) * 2) calc(var(--fes-step) * 2) calc(var(--fes-step) * 2) calc(var(--fes-step) * 2.5);
  }

  .fes.has-focus .fes-block {
    opacity: 1;
  }

  /* Nothing to hover here, and the list already says who is in the fight. */
  .fes-hint {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fes-sector,
  .fes-sector-lit,
  .fes-sector-wash,
  .fes-sector-figure,
  .fes-figure-bloom,
  .fes-wedge,
  .fes-wedge-arc,
  .fes-wedge-rim,
  .fes-ray,
  .fes-ring,
  .fes-block,
  .fes-block-detail,
  .fes-fade-enter-active,
  .fes-fade-leave-active,
  .fes-fade-move {
    transition: none;
  }

  .fes-spinner,
  .fes-wedge.is-pending .fes-wedge-arc {
    animation: none;
  }
}
</style>
